import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Skeleton,
  useTheme,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Help, Lock as LockIcon } from '@mui/icons-material';
import { CertificationStatusEnumValue, getStatusConfig } from 'enums/CertificationStatusEnum';
import { CriteriaTypeDetailTypeEnumValue } from 'enums/CriteriaTypeDetailTypeEnum';
import { useSubmission, useUpdateSubmission } from 'services/Data/SubmissionServices';
import { useCriteria, useCriteriaOptions, useCriteriaTypes, useCriteriaTypeDetails } from 'services/Data/CriteriaServices';
import { certificationToolCreatePatchDocument } from 'services/Serializers/CertificationToolPatchDocument';
import { detailAppliesToSubmission, orderDetailsByHierarchy } from 'services/Utilities/criteriaDetailUtils';
import { isSubmissionQuarterExpired } from 'services/Utilities/submissionUtils';
import WizardView from './components/WizardView';
import ProductLineSelectionStep from './Step1/ProductLineSelectionStep';
import CriteriaOptionSelectionStep from './Step2/CriteriaOptionSelectionStep';
import SidebarView from './Step3/SidebarView';
import SummaryView from './Step4/SummaryView';
import SubmissionHeader from './components/SubmissionHeader';
import SubmissionActionButtons from '../components/SubmissionActionButtons';
import { getCertificationUploadPhotoTourPage } from '../tourSteps/certificationUploadPhotoTourPage';
import GlobalTour from '../tourSteps/GlobalTour';

const SubmissionForm = () => {
  const { submissionId, stepNumber } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();
  const account = useSelector(state => state.AccountReducer);

  const basePath = '/dealer/certification';

  const {
    data: submission,
    isLoading: isLoadingSubmission,
    error: submissionError,
    refetch: refetchSubmission,
  } = useSubmission(submissionId);
  // criteria is fetched (likely for cache / data completeness) but not directly used in this component
  const { data: _criteria, isLoading: isLoadingCriteria, error: criteriaError } = useCriteria(submission?.criteria_Id);
  const { data: criteriaOptions, isLoading: isLoadingCriteriaOptions, error: criteriaOptionsError } = useCriteriaOptions();
  const { data: criteriaTypes, isLoading: isLoadingCriteriaTypes, error: criteriaTypesError } = useCriteriaTypes();
  const {
    data: criteriaTypeDetails,
    isLoading: isLoadingCriteriaTypeDetails,
    error: criteriaTypeDetailsError,
  } = useCriteriaTypeDetails(submissionId);
  const updateSubmission = useUpdateSubmission(submissionId);

  const step = useMemo(() => parseInt(stepNumber, 10) || 1, [stepNumber]);

  const isLoading =
    isLoadingSubmission ||
    isLoadingCriteria ||
    isLoadingCriteriaOptions ||
    isLoadingCriteriaTypes ||
    isLoadingCriteriaTypeDetails;

  const error = submissionError || criteriaError || criteriaOptionsError || criteriaTypesError || criteriaTypeDetailsError;

  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourSteps, setTourSteps] = useState([]);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitErrorDialogOpen, setIsSubmitErrorDialogOpen] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  const hasNonEmptyValue = useCallback(value => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  }, []);

  useEffect(() => {
    setTourSteps(getCertificationUploadPhotoTourPage(t));
  }, [t]);

  const nextTourStep = useCallback(() => {
    setTourStep(prev => {
      const next = prev + 1;
      if (next < tourSteps.length) return next;
      setShowTour(false);
      return 0;
    });
  }, [tourSteps.length]);

  const closeTour = useCallback(() => {
    setShowTour(false);
    setTourStep(0);
  }, []);

  const startTour = useCallback(() => {
    const cleaned = (tourSteps || []).filter(s => s?.target && document.querySelector(s.target));
    setTourSteps(cleaned);
    setTourStep(0);
    setShowTour(true);
  }, [tourSteps]);

  useEffect(() => {
    if (!stepNumber && submissionId) {
      history.replace(`${basePath}/submissions/${submissionId}/step/1`);
    }
  }, [basePath, history, stepNumber, submissionId]);

  useEffect(() => {
    if (!submission || !account?.dealer?.id) return;
    if (submission?.dealer?.id && submission.dealer.id !== account.dealer.id) {
      history.replace(basePath);
    }
  }, [account?.dealer?.id, basePath, history, submission]);

  const handleSaveAndQuit = useCallback(() => history.push(`${basePath}/submissions`), [basePath, history]);

  const statusConfig = useMemo(() => getStatusConfig(submission?.status), [submission?.status]);
  const isAwaitingReview = useMemo(() => submission?.status === CertificationStatusEnumValue.AwaitingReview, [
    submission?.status,
  ]);
  const isReviewInProgress = useMemo(() => submission?.status === CertificationStatusEnumValue.ReviewInProgress, [
    submission?.status,
  ]);
  const isFinalStatus = useMemo(() => Boolean(statusConfig?.isFinal), [statusConfig?.isFinal]);

  const isQuarterExpired = useMemo(() => (submission ? isSubmissionQuarterExpired(submission) : false), [submission]);

  const canEdit = useMemo(() => !isAwaitingReview && !isReviewInProgress && !isFinalStatus && !isQuarterExpired, [
    isAwaitingReview,
    isReviewInProgress,
    isFinalStatus,
    isQuarterExpired,
  ]);

  const submissionCriteriaId = submission?.criteria_Id || null;
  const attemptedOptionId = submission?.attemptedCriteriaOption_Id;
  const requiresHeroUnitSelection = submission?.criteria?.isHeroUnitVisible !== false;
  const heroUnitProductLineId = submission?.heroUnitProductLine_Id || submission?.heroUnitProductLine?.id || null;

  const dealerProductLineIds = useMemo(() => {
    return (
      submission?.dealer?.dealerProductLines?.map(line => line.productLine_Id || line.productLine?.id).filter(Boolean) || []
    );
  }, [submission?.dealer?.dealerProductLines]);

  const submissionDetailValueByDetailId = useMemo(() => {
    const map = new Map();

    (submission?.submissionDetails ?? []).forEach(detail => {
      if (!detail?.criteriaTypeDetail_Id) return;
      map.set(detail.criteriaTypeDetail_Id, detail.value ?? '');
    });

    return map;
  }, [submission?.submissionDetails]);

  const availableCriteriaOptions = useMemo(() => {
    if (!criteriaOptions || !submissionCriteriaId) return [];
    return criteriaOptions.filter(option => {
      if (!option?.isAvailableForDealerSelection) return false;
      const matchesJoin = (option.criteriaCriteriaOptions || []).some(join => join?.criteria_Id === submissionCriteriaId);
      const matchesLegacy = option.criteria_Id === submissionCriteriaId;
      return matchesJoin || matchesLegacy;
    });
  }, [criteriaOptions, submissionCriteriaId]);

  const hasCriteriaSelectionStep = useMemo(() => availableCriteriaOptions.length > 0, [availableCriteriaOptions.length]);

  const shouldIncludeDetail = useCallback(
    detail =>
      detailAppliesToSubmission(detail, {
        attemptedCriteriaOptionId: attemptedOptionId,
        heroUnitProductLineId,
        dealerProductLineIds,
        submissionDetailValueByDetailId,
        requiresHeroUnitSelection,
        hasAvailableCriteriaOptions: hasCriteriaSelectionStep,
      }),
    [
      attemptedOptionId,
      heroUnitProductLineId,
      dealerProductLineIds,
      submissionDetailValueByDetailId,
      requiresHeroUnitSelection,
      hasCriteriaSelectionStep,
    ],
  );

  const eligibleCriteriaTypes = useMemo(() => {
    if (!criteriaTypes || !submission?.criteria_Id) return [];

    return criteriaTypes
      .filter(ct => ct.criteria_Id === submission.criteria_Id)
      .filter(ct => {
        const optionIds = (ct.criteriaTypeCriteriaOptions || []).map(join => join?.criteriaOption_Id);
        if (attemptedOptionId && !optionIds.includes(attemptedOptionId)) return false;

        const productLineIds = (ct.criteriaTypeProductLines || []).map(join => join?.productLine_Id);
        if (requiresHeroUnitSelection) {
          if (!heroUnitProductLineId || !productLineIds.includes(heroUnitProductLineId)) return false;
        } else if (heroUnitProductLineId && !productLineIds.includes(heroUnitProductLineId)) return false;

        const dealerLines = (ct.criteriaTypeDealerProductLines || []).map(join => join?.productLine_Id) || [];
        if (dealerLines.length && !dealerLines.some(id => dealerProductLineIds.includes(id))) return false;

        return true;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [
    criteriaTypes,
    submission?.criteria_Id,
    attemptedOptionId,
    heroUnitProductLineId,
    requiresHeroUnitSelection,
    dealerProductLineIds,
  ]);

  const eligibleCriteriaTypeIds = useMemo(() => new Set(eligibleCriteriaTypes.map(ct => ct.id)), [eligibleCriteriaTypes]);

  const eligibleDetailsByType = useMemo(() => {
    if (!criteriaTypeDetails || (requiresHeroUnitSelection && !heroUnitProductLineId)) return new Map();

    const grouped = new Map();
    criteriaTypeDetails.forEach(detail => {
      if (!detail?.criteriaType_Id) return;
      const existing = grouped.get(detail.criteriaType_Id) ?? [];
      existing.push(detail);
      grouped.set(detail.criteriaType_Id, existing);
    });

    const filtered = new Map();
    grouped.forEach((details, criteriaTypeId) => {
      if (!eligibleCriteriaTypeIds.has(criteriaTypeId)) return;
      const eligible = details.filter(detail => shouldIncludeDetail(detail));
      const ordered = orderDetailsByHierarchy(eligible);
      if (ordered.length > 0) filtered.set(criteriaTypeId, ordered);
    });

    return filtered;
  }, [criteriaTypeDetails, eligibleCriteriaTypeIds, heroUnitProductLineId, requiresHeroUnitSelection, shouldIncludeDetail]);

  const buildRequiredDetailIds = useCallback(
    predicate => {
      const ids = new Set();
      eligibleDetailsByType.forEach(details => {
        details.forEach(detail => {
          if (predicate(detail)) ids.add(detail.id);
        });
      });
      return ids;
    },
    [eligibleDetailsByType],
  );

  const requiredPhotoDetailIds = useMemo(
    () =>
      buildRequiredDetailIds(
        detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Photo && detail?.isRequired !== false,
      ),
    [buildRequiredDetailIds],
  );

  const requiredInputDetailIds = useMemo(
    () =>
      buildRequiredDetailIds(
        detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Input && detail?.isRequired !== false,
      ),
    [buildRequiredDetailIds],
  );

  const requiredSelectDetailIds = useMemo(
    () =>
      buildRequiredDetailIds(
        detail =>
          detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Dropdown && detail?.isRequired !== false,
      ),
    [buildRequiredDetailIds],
  );

  const submissionDetailsByDetailId = useMemo(() => {
    const map = new Map();

    (submission?.submissionDetails ?? []).forEach(sd => {
      if (!map.has(sd.criteriaTypeDetail_Id)) {
        map.set(sd.criteriaTypeDetail_Id, sd);
      }
    });

    return map;
  }, [submission?.submissionDetails]);

  const buildCompletedDetailIds = useCallback(
    (requiredIds, completionPredicate) => {
      const completed = new Set();
      requiredIds.forEach(detailId => {
        const submissionDetail = submissionDetailsByDetailId.get(detailId);
        if (completionPredicate(submissionDetail)) {
          completed.add(detailId);
        }
      });
      return completed;
    },
    [submissionDetailsByDetailId],
  );

  const completedPhotoDetailIds = useMemo(
    () => buildCompletedDetailIds(requiredPhotoDetailIds, sd => sd?.files && sd.files.length > 0),
    [requiredPhotoDetailIds, buildCompletedDetailIds],
  );

  const completedInputDetailIds = useMemo(
    () => buildCompletedDetailIds(requiredInputDetailIds, sd => hasNonEmptyValue(sd?.value)),
    [requiredInputDetailIds, buildCompletedDetailIds, hasNonEmptyValue],
  );

  const completedSelectDetailIds = useMemo(
    () => buildCompletedDetailIds(requiredSelectDetailIds, sd => hasNonEmptyValue(sd?.value)),
    [requiredSelectDetailIds, buildCompletedDetailIds, hasNonEmptyValue],
  );

  const hasHeroUnit = useMemo(() => (requiresHeroUnitSelection ? !!submission?.heroUnitProductLine_Id : true), [
    requiresHeroUnitSelection,
    submission?.heroUnitProductLine_Id,
  ]);

  const hasCriteriaOption = useMemo(() => !!submission?.attemptedCriteriaOption_Id, [
    submission?.attemptedCriteriaOption_Id,
  ]);

  const hasCriteriaOptionOrIsOptional = useMemo(() => hasCriteriaOption || !hasCriteriaSelectionStep, [
    hasCriteriaOption,
    hasCriteriaSelectionStep,
  ]);

  const hasCompletedAllRequiredPhotos = useMemo(
    () => requiredPhotoDetailIds.size === 0 || completedPhotoDetailIds.size === requiredPhotoDetailIds.size,
    [requiredPhotoDetailIds.size, completedPhotoDetailIds.size],
  );

  const hasCompletedAllRequiredInputs = useMemo(
    () => requiredInputDetailIds.size === 0 || completedInputDetailIds.size === requiredInputDetailIds.size,
    [requiredInputDetailIds.size, completedInputDetailIds.size],
  );

  const hasCompletedAllRequiredSelects = useMemo(
    () => requiredSelectDetailIds.size === 0 || completedSelectDetailIds.size === requiredSelectDetailIds.size,
    [requiredSelectDetailIds.size, completedSelectDetailIds.size],
  );

  const isReadyForReview = useMemo(
    () =>
      hasHeroUnit &&
      hasCriteriaOptionOrIsOptional &&
      hasCompletedAllRequiredPhotos &&
      hasCompletedAllRequiredInputs &&
      hasCompletedAllRequiredSelects,
    [
      hasHeroUnit,
      hasCriteriaOptionOrIsOptional,
      hasCompletedAllRequiredPhotos,
      hasCompletedAllRequiredInputs,
      hasCompletedAllRequiredSelects,
    ],
  );

  const canSubmitForReview = useMemo(() => canEdit && isReadyForReview, [canEdit, isReadyForReview]);

  const handleSubmitForReview = useCallback(async () => {
    if (!submissionId) return;
    if (!canSubmitForReview) return;

    try {
      const patchDocument = certificationToolCreatePatchDocument({
        status: CertificationStatusEnumValue.AwaitingReview,
      });
      await updateSubmission.mutateAsync(patchDocument);
      setIsSubmitDialogOpen(true);
    } catch (err) {
      const missingCount = err?.response?.data?.missingCount;
      const message = err?.response?.data?.message;

      if (missingCount > 0) {
        setSubmitErrorMessage(t('submission.submitRequirementsMissingCount', { count: missingCount }));
      } else if (message) {
        setSubmitErrorMessage(message);
      } else {
        setSubmitErrorMessage(t('submission.submitRequirementsFailed'));
      }
      setIsSubmitErrorDialogOpen(true);
      if (refetchSubmission) {
        await refetchSubmission();
      }
    }
  }, [canSubmitForReview, refetchSubmission, submissionId, t, updateSubmission]);

  const handleSubmitDialogClose = useCallback(() => {
    setIsSubmitDialogOpen(false);
    history.push(`${basePath}/submissions`);
  }, [basePath, history]);

  const handleSubmitErrorDialogClose = useCallback(() => {
    setIsSubmitErrorDialogOpen(false);
  }, []);

  const renderStep = useCallback(() => {
    if (step === 1) return <ProductLineSelectionStep isReadOnly={!canEdit} />;
    if (step === 2) return <CriteriaOptionSelectionStep isReadOnly={!canEdit} />;
    if (step === 3) return <SidebarView isReadOnly={!canEdit} hasAvailableCriteriaOptions={hasCriteriaSelectionStep} />;
    return <SummaryView isReadOnly={!canEdit} hasAvailableCriteriaOptions={hasCriteriaSelectionStep} />;
  }, [step, canEdit, hasCriteriaSelectionStep]);

  if (isLoading) {
    return (
      <Box>
        <Box
          sx={{
            maxWidth: { xs: '100%', md: 1200, lg: 1400, xl: 1600 },
            mx: 'auto',
            px: { xs: 2, md: 3 },
            pb: { xs: 3, md: 4 },
          }}>
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Skeleton variant="text" width={140} height={36} />
            <Paper
              elevation={8}
              sx={{
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}>
              <Skeleton variant="text" width="55%" height={44} />
              <Skeleton variant="text" width="35%" height={30} />
              <Skeleton variant="rounded" height={96} sx={{ mt: 2 }} />
            </Paper>
          </Box>

          <Paper
            elevation={4}
            sx={{
              borderRadius: 2,
              p: { xs: 2, md: 3 },
              mb: { xs: 3, md: 4 },
              border: theme => `1px solid ${theme.palette.divider}`,
            }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton variant="rectangular" width={120} height={10} />
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton variant="rectangular" width={120} height={10} />
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton variant="rectangular" width={120} height={10} />
              <Skeleton variant="circular" width={36} height={36} />
            </Box>
          </Paper>

          <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
            <Skeleton variant="text" width="30%" height={32} />
            <Skeleton variant="rounded" height={72} sx={{ mt: 2 }} />
            <Skeleton variant="rounded" height={72} sx={{ mt: 2 }} />
            <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
          </Paper>

          <Box sx={{ display: 'none' }} aria-hidden>
            <CircularProgress />
            <Typography>{t('submissions.loading')}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('submissions.loadingFailed')} {error.message}
        </Alert>
        <Button variant="outlined" onClick={() => history.push(`${basePath}/submissions`)}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="warning">{t('submission.notFound')}</Alert>
        <Button variant="outlined" onClick={() => history.push(`${basePath}/submissions`)} sx={{ mt: 2 }}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <IconButton
        onClick={startTour}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#FFD200',
          color: '#000',
          zIndex: 1000,
          '&:hover': {
            bgcolor: '#B8941E',
          },
        }}>
        <Help />
      </IconButton>

      <Box
        sx={{
          maxWidth: { xs: '100%', md: 1200, lg: 1400, xl: 1600 },
          mx: 'auto',
          px: { xs: 2, md: 3 },
          pb: { xs: 3, md: 4 },
        }}>
        <SubmissionHeader submission={submission} />

        {isAwaitingReview ? (
          <Alert
            severity="info"
            icon={<LockIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.15),
            }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('submission.awaitingReviewLockedTitle')}
            </Typography>
            <Typography variant="body2">{t('submission.awaitingReviewLockedDescription')}</Typography>
          </Alert>
        ) : null}

        {isQuarterExpired && !isFinalStatus ? (
          <Alert
            severity="warning"
            icon={<LockIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.15),
            }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('submission.quarterExpiredLockedTitle')}
            </Typography>
            <Typography variant="body2">{t('submission.quarterExpiredLockedDescription')}</Typography>
          </Alert>
        ) : null}

        {isReviewInProgress ? (
          <Alert
            severity="success"
            icon={<LockIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.15),
            }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('submission.reviewInProgressLockedTitle')}
            </Typography>
            <Typography variant="body2">{t('submission.reviewInProgressLockedDescription')}</Typography>
          </Alert>
        ) : null}

        {isFinalStatus ? (
          <Alert
            severity="success"
            icon={<LockIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.15),
            }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('submission.finalStatusLockedTitle')}
            </Typography>
            <Typography variant="body2">{t('submission.finalStatusLockedDescription')}</Typography>
          </Alert>
        ) : null}

        <WizardView />

        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          {renderStep()}
        </Paper>

        {step === 4 && canEdit && !isReadyForReview ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('submission.submitRequirementsIncomplete')}
          </Alert>
        ) : null}

        {step === 4 && canEdit ? (
          <SubmissionActionButtons
            onSaveAndQuit={handleSaveAndQuit}
            onSubmit={handleSubmitForReview}
            submission={submission}
            disabled={!canSubmitForReview}
          />
        ) : null}
      </Box>

      <Dialog open={isSubmitDialogOpen} onClose={handleSubmitDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('submission.submittedForReviewTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('submission.submittedForReviewDescription')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="contained" onClick={handleSubmitDialogClose}>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isSubmitErrorDialogOpen} onClose={handleSubmitErrorDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('submission.submitRequirementsFailedTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{submitErrorMessage}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="contained" onClick={handleSubmitErrorDialogClose}>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <GlobalTour show={showTour} steps={tourSteps} stepIndex={tourStep} onClose={closeTour} onNext={nextTourStep} t={t} />
    </Box>
  );
};

export default SubmissionForm;
